import { EstateQrPhoto } from './estate-qr-page.models';

export function isAcceptedEstateQrPhotoType(file: File): boolean {
  return ['image/jpeg', 'image/png'].includes(file.type) || /\.(jpg|jpeg|png)$/i.test(file.name);
}

export function getEstateQrPhotoBytes(photos: EstateQrPhoto[]): number {
  return photos.reduce((sum, photo) => sum + photo.file.size, 0);
}

export async function createEstateQrPhoto(file: File): Promise<EstateQrPhoto> {
  const optimizedFile = await optimizeEstateQrPhoto(file);
  return {
    file: optimizedFile,
    previewDataUrl: await readFileAsDataUrl(optimizedFile)
  };
}

async function optimizeEstateQrPhoto(file: File): Promise<File> {
  try {
    const sourceDataUrl = await readFileAsDataUrl(file);
    const image = await loadImage(sourceDataUrl);
    const { width, height } = getScaledDimensions(image.width, image.height, 1600);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return file;

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.82);
    if (!blob) return file;

    return new File([blob], renameEstateQrPhoto(file.name), { type: 'image/jpeg' });
  } catch (error) {
    console.warn('Failed to optimize estate QR photo', error);
    return file;
  }
}

function readFileAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Image load error'));
    image.src = dataUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function getScaledDimensions(width: number, height: number, maxDimension: number): { width: number; height: number } {
  const ratio = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio))
  };
}

function renameEstateQrPhoto(fileName: string): string {
  const baseName = fileName.replace(/\.[^.]+$/, '').trim() || 'photo';
  return `${baseName}.jpg`;
}
